const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/stringUtils.ts';
import Imm from 'immutable';
import _ from 'lodash';
import Log from '../services/KisLoggerService';
import { DollarDSL } from './DollarDSL/DollarDSL';

const umlautMap: Record<string, string[]> = {
    a: ['à', 'á', 'â', 'ä', 'ã'],
    c: ['ç'],
    e: ['è', 'é', 'ê', 'ë'],
    i: ['ì', 'í', 'î', 'ï'],
    n: ['ñ'],
    o: ['ò', 'ó', 'ô', 'ö', 'õ'],
    u: ['ù', 'ú', 'û', 'ü'],
};

/**
 * Compare two strings (e.g. names of two patients). Case-insensitive, and treats umlauts correctly.
 * @return -1 if a comes before b; 1 if a comes after b; 0 if they are identical
 */
export function compareString(a: string, b: string): number {
    const prepare = (s: string) => {
        let p = s.toLowerCase();
        Object.keys(umlautMap).forEach(base => {
            umlautMap[base].forEach(umlaut => {
                p = p.split(umlaut).join(base);
            });
        });
        return p;
    };

    const pa = prepare(a);
    const pb = prepare(b);
    if (pa > pb) return 1;
    if (pa < pb) return -1;
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
}

/**
 * Compares two strings containing numbers correctly (i.e. "6" comes before "46"). Similar
 * to String.prototype.localeCompare(), which is not available in mobile browsers yet. System:
 * 1.  Get the position of the first number in the string of 'a' and 'b'.
 * 2.a If both string contains a number at the beginning, parse this numbers and compare it.
 * 2.b Else, compare the strings. Only the number of characters of the strings are compared, which do not contain a
 *     number in BOTH strings.
 * 3.a If there is a difference between the compared numbers or strings, the result is returned.
 * 3.b Else, the compared part of the strings is excluded and the rest of the strings is compared (starting at 1.).
 *
 * @param a first string to compare
 * @param b second string to compare
 * @param desc Optional parameter which by default (ie. if not given) is false. If false, the strings are compared to
 * sort ascending. If false, the strings are compared to sort descending.
 * @returns number Negative when `a` comes before `b` when sorted alphabetically. Positive `b` comes first. 0 when
 * the strings are equal.
 */
export function compareStringWNumber(a: string | undefined, b: string | undefined, desc: boolean = false): number {
    let posNextNumberA: number;
    let posNextNumberB: number;
    let charsToTest: number;
    let charsToTestA: number;
    let charsToTestB: number;
    let result: number = 0;
    const sorting = desc ? -1 : 1;

    a = typeof a === 'undefined' ? '' : a;
    b = typeof b === 'undefined' ? '' : b;

    while (result === 0) {
        // check if one of the strings is empty
        if (a.length === 0) {
            if (b.length !== 0) {
                result = -1;
            }
            break;
        } else if (b.length === 0) {
            result = 1;
            break;
        }

        // get the position of next number in each string
        posNextNumberA = a.search(/[0-9]/);
        posNextNumberB = b.search(/[0-9]/);

        if (posNextNumberA === 0 && posNextNumberB === 0) {
            // compare two numbers
            // console.log('Comparing two numbers.');
            charsToTestA = Math.min(a.search(/[^0-9]/), a.length);
            charsToTestA = charsToTestA >= 0 ? charsToTestA : a.length;
            charsToTestB = Math.min(b.search(/[^0-9]/), b.length);
            charsToTestB = charsToTestB >= 0 ? charsToTestB : b.length;
            result = parseInt(a.substring(0, charsToTestA), 10) - parseInt(b.substring(0, charsToTestB), 10);
            /* console.log('\"' + parseInt(a.substr(0, charsToTestA), 10) + '\" - \"' + parseInt(b.substr(0, charsToTestB), 10) + '\"' +
             ' ->' +
             ' result = ' + result); */
            a = a.substring(charsToTestA);
            b = b.substring(charsToTestB);
        } else {
            // compare a string with a string or number
            posNextNumberA = posNextNumberA > 0 ? posNextNumberA : Infinity;
            posNextNumberB = posNextNumberB > 0 ? posNextNumberB : Infinity;
            charsToTest = Math.min(a.length, b.length, Math.max(posNextNumberA, posNextNumberB));
            result = compareString(a.substring(0, charsToTest), b.substring(0, charsToTest));
            // console.log('\"' + a.substr(0, charsToTest) + '\" vs. \"' + b.substr(0, charsToTest) + '\" -> result = ' + result);
            a = a.substring(charsToTest);
            b = b.substring(charsToTest);
        }
    }
    return sorting * result;
}

/**
 * Checks to see if the given text contains one or more of the substrings given.
 *
 * @param text (in): The string to be examined
 * @param subs (in): Array of substrings to be checked against the text
 * @return: true if the given text contains any of the substrings from the array;
 *    false if none of the substrings occur in text.
 */
export function strContainsSome(text: string, subs: string[]): boolean {
    return subs.reduce((result: boolean, sub: string) => {
        if (result) {
            return true;
        } else {
            return text.indexOf(sub) >= 0;
        }
    }, false);
}

/**
 * Checks to see if the given text contains any words consisting of none but
 * upper-case letters. Words consisting of both upper-case letters and other
 * characters will not contribute to the count.
 *
 * @param text (in): The string to be examined
 * @return: 0 if the string does not contain any words purely based on upper-
 *    case letters; otherwise a value > 0 indicating the maximum word length.
 */
export function strContainsUppercaseWordLength(text: string): number {
    let upperLen = 0;
    let maxUpperLen = 0;
    const wordDelim = ' \t\n\r-/,.;:()[]{}';
    let s = 0;

    for (let i = 0; i <= text.length; i++) {
        const c = i < text.length ? text.charAt(i) : null;
        const isUpper = c && c.toUpperCase() && c !== c.toLowerCase();
        const isDelim = !c || wordDelim.indexOf(c) >= 0;

        switch (s) {
            case 0: // indeterminate
                if (isUpper) {
                    upperLen = 1;
                    s = 1;
                } else if (!isDelim) {
                    s = 2;
                }
                break;

            case 1: // uppercase
                if (isUpper) {
                    upperLen++;
                } else if (isDelim) {
                    if (upperLen > maxUpperLen) {
                        maxUpperLen = upperLen;
                    }
                    upperLen = 0;
                    s = 0;
                } else {
                    upperLen = 0;
                    s = 2;
                }
                break;

            case 2: // not uppercase
                if (isDelim) {
                    s = 0;
                }
                break;
        }
    }

    return maxUpperLen;
}

/**
 * Adds the given character multiple times to the left of the input string until the desired length is reached.
 * @return The input string left-padded with the given character
 */
export function strLPad(input: undefined | string | null, len: number, padWith: string = '0') {
    if (padWith.length !== 1) {
        throw new Error('strLPad: Bad argument padWith');
    }

    const theInput = input || '';
    const numChars = len - theInput.length;

    if (numChars <= 0) {
        return theInput; // desired length already reached
    }

    return `${Array(numChars).fill(padWith).join('')}${theInput}`;
}

/**
 * Adds the given character multiple times to the right of the input string until the desired length is reached.
 * @return The input string right-padded with the given character
 */
export function strRPad(input: undefined | string | null, len: number, padWith: string = '0') {
    if (padWith.length !== 1) {
        throw new Error('strRPad: Bad argument padWith');
    }

    const theInput = input || '';
    const numChars = len - theInput.length;

    if (numChars <= 0) {
        return theInput; // desired length already reached
    }

    return `${theInput}${Array(numChars).fill(padWith).join('')}`;
}

/**
 * Used by patdash to parse the $ notation within configuration strings.
 * @param s the string from the config, possibly contain $ values
 * @param map the data record (e.g. a KisOrder, KisReport, etc.)
 * @param defaultVal value to be used when a $ value evaluates to an empty string
 * @return The string s with any parseable $ value replaced
 */
export function replaceFields(s: string, map: Imm.Map<string, any>, defaultVal: string = '') {
    const dsl = new DollarDSL(map);
    return dsl.parse(s, defaultVal);
}

/**
 * Calls replaceFields recursively on all members.
 * @param member tree or member to be transformed; may be null or undefined
 * @param map passed down to replaceFields
 */
export function replaceFieldsDeep(member: any, map: Imm.Map<string, any>): any {
    if (typeof member === 'string') {
        return replaceFields(member, map);
    } else if (member === null || typeof member === 'undefined' || typeof member === 'number') {
        return member;
    } else if (Array.isArray(member)) {
        return member.map((entry: any) => replaceFieldsDeep(entry, map));
    } else if (Imm.Iterable.isIterable(member)) {
        return member.map((entry: any) => replaceFieldsDeep(entry, map));
    }

    throw new Error(`replaceFieldsDeep: Member has unsupported type: ${JSON.stringify(member)}`);
}

/**
 * Returns the val with a leading space if val is not empty. If val is empty or contains only spaces, an empty string will be returned.
 * @param {string} val
 * @return {string}
 */
export function getStringWLeadingSpace(val: string): string {
    return !_.isEmpty(val) ? ` ${val}` : '';
}

/**
 * Tests a key (FKEY, FCODE, KAT, ...) against an SQL-like LIKE pattern
 * @param pattern Regexp pattern, must be valid, may contain '%' which is used similar to the SQL LIKE comparator
 * @param value value to be matched against the pattern
 */
export function wildcardMatch(pattern: string, value: unknown): boolean {
    if (!value || typeof value !== 'string') {
        return false;
    }
    try {
        const patternForRegExp = `^${pattern}$`.replace('%', '[A-Z0-9]*');
        return new RegExp(patternForRegExp).test(value.toUpperCase());
    } catch {
        Log.error('Invalid pattern for wildcard match: ', pattern);
    }
    return false;
}

/**
 * @return The string with the first character being capitalized
 */
export function strInitCap(s: string) {
    return `${(s || '').slice(0, 1).toUpperCase()}${(s || '').slice(1)}`;
}

/**
 * @return A string that does not exceed the given length.
 */
export function limitLength(s: string, maxLen: number): string {
    if (s.length <= maxLen) {
        return s;
    } else {
        return `${s.slice(0, maxLen - 3)}...`;
    }
}

/**
 * Similar to JSON.stringify, except that it sorts the keys alphabetically, thus
 * ensuring a defined order.
 * @param val the value to stringify
 * @param filter optional callback to remove key/value pairs beforehand
 * @param mapper optional callback to map each value to a new one beforehand
 * @param stringifyKeys true=also stringify keys of an object (JSON); false=don't (JS object syntax)
 * @param dense true=avoid unnecessary whitespace; false=add some minimal whitespace for better readability
 * @return A string
 */
export function stableStringify(
    val: any,
    filter?: (val: any, key: string | number) => boolean,
    mapper?: (val: any, key: string | number) => any,
    stringifyKeys: boolean = true,
    dense: boolean = true
) {
    if (val === undefined) {
        return 'undefined';
    } else if (val === null) {
        return 'null';
    } else if (typeof val === 'string') {
        return JSON.stringify(val);
    } else if (Array.isArray(val)) {
        const values: string = val
            .filter((subVal, key) => {
                if (filter !== undefined) {
                    return filter(subVal, key);
                } else {
                    return true;
                }
            })
            .map((subVal, key) => {
                if (mapper !== undefined) {
                    const ret = mapper(subVal, key);
                    if (ret !== undefined) {
                        return ret;
                    }
                }
                return stableStringify(subVal, filter, mapper, stringifyKeys, dense);
            })
            .join(dense ? ',' : ', ');
        return `[${values}]`;
    } else if (typeof val === 'object') {
        const values: string = Object.keys(val)
            .sort() // the whole point of this function!!
            .filter(key => {
                const subVal = val[key];

                if (filter !== undefined) {
                    return filter(subVal, key);
                } else {
                    return true;
                }
            })
            .map(key => {
                const subVal = val[key];
                const skey = !stringifyKeys ? key : JSON.stringify(key);
                if (mapper !== undefined) {
                    const ret = mapper(subVal, key);
                    if (ret !== undefined) {
                        return `${skey}${dense ? ':' : ': '}${ret}`;
                    }
                }
                return `${skey}${dense ? ':' : ': '}${stableStringify(subVal, filter, mapper, stringifyKeys, dense)}`;
            })
            .join(dense ? ',' : ', ');
        return dense ? `{${values}}` : `{ ${values} }`;
    } else {
        return `${val}`;
    }
}

/**
 * Converts a comma-separated list into an array, trimming parts and removing empty members.
 * @return An array (may be empty)
 */
export function explodeCommaSeparated(haystack: string | null | undefined): string[] {
    return (haystack ?? '')
        .split(',')
        .map(s => s.trim())
        .filter(s => !!s);
}

export const cleanRedundantSpacingAndUndefined = (value: string) => {
    return (value || '')
        .replace(/undefined/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

export function convertRtfToPlain(rtfString: unknown, preserveLF: boolean = false) {
    if (typeof rtfString !== 'string' || !rtfString) {
        return '';
    }
    let plainString = rtfString.replace(/\\par[d]?/g, preserveLF ? '%%CR%%' : '');
    plainString = plainString.replace(/\{\*?\\[^{}]+}|[{}]|\\\n?[A-Za-z]+\n?(?:-?\d+)?[ ]?/g, '').trim();
    plainString = plainString.replace(/[\r\n]/g, ' ').replace(/  +/g, ' ');
    plainString = plainString.replace(/\\'../g, (code: string) => {
        return String.fromCharCode(Number.parseInt(code.substring(2), 16));
    });
    plainString = plainString.replace(/ *%%CR%% */g, '\n');
    return plainString;
}

export function wrapPlainWithRtfMarkup(plainString: string) {
    return `{\\rtf1\\ansi\\ansicpg1252\\deff0\\deflang2055{\\fonttbl{\\f0\\fswiss\\fprq2\\fcharset0 Arial;}}\r\n{\\colortbl ;\\red0\\green0\\blue0;}\r\n{\\*\\generator Riched20 5.40.11.2210;}\\viewkind4\\uc1\\pard\\cf1\\highlight2\\f0\\fs18 ${newlinesToPar(plainString)}\\par\r\n}\r\n`;
}

export function newlinesToPar(plainString: string) {
    return plainString.replace(/\n/g, '\\par\n');
}

/**
 * Finds the common prefix of all the entries.
 * @param arr the strings to be processed
 * @param boundary specifies a character that defines a boundary for the prefix
 * @return the common prefix, or the empty string if there is no common prefix
 */
export function getCommonPrefix(arr: string[], boundary: string = ''): string {
    if (!arr.length) return '';
    let minLen = Number.MAX_SAFE_INTEGER;

    arr.forEach(s => {
        if (s.length < minLen) minLen = s.length;
    });

    if (!minLen) return ''; // at least one entry is empty, no common prefix

    let knownPrefix = '';
    let result = '';

    while (knownPrefix.length < minLen) {
        // Invariant: knownPrefix is a prefix of all the entries, ignoring boundary. That means knownPrefix is also a
        // prefix of arr[0]. Since the length of knownPrefix is less than minLen, arr[0] must have at least one
        // additional character.
        const maybePrefix = `${knownPrefix}${arr[0][knownPrefix.length]}`;
        let ok = true;

        for (const entry of arr) {
            if (!entry.startsWith(maybePrefix)) {
                // maybePrefix is not a prefix of this entry.
                ok = false;
                break;
            }
        }

        if (!ok) break;

        if (boundary.length > 0) {
            // Set result only when it touches a boundary.
            if (maybePrefix.endsWith(boundary)) {
                result = maybePrefix;
            }
        } else {
            result = maybePrefix; // whatever prefix we found will be the result
        }

        knownPrefix = maybePrefix;
    }

    if (!result && knownPrefix) {
        // An empty result indicates that the prefix stopped before it reached a boundary.
        return knownPrefix;
    }

    return result;
}

/**
 * Finds the common postfix of all the entries.
 * @param arr the strings to be processed
 * @param boundary specifies a character that defines a boundary for the postfix
 * @return the common postfix, or the empty string if there is no common postfix
 */
export function getCommonPostfix(arr: string[], boundary: string = ''): string {
    if (!arr.length) return '';
    let minLen = Number.MAX_SAFE_INTEGER;

    arr.forEach(s => {
        if (s.length < minLen) minLen = s.length;
    });

    if (!minLen) return ''; // at least one entry is empty, no common postfix

    let knownPostfix = '';
    let result = '';

    while (knownPostfix.length < minLen) {
        // Invariant: knownPostfix is a postfix of all the entries, ignoring boundary. That means knownPostfix is also a
        // postfix of arr[0]. Since the length of knownPostfix is less than minLen, arr[0] must have at least one
        // additional character.
        const maybePostfix = `${arr[0][arr[0].length - 1 - knownPostfix.length]}${knownPostfix}`;
        let ok = true;

        for (const entry of arr) {
            if (!entry.endsWith(maybePostfix)) {
                // maybePostfix is not a postfix of this entry.
                ok = false;
                break;
            }
        }

        if (!ok) break;

        if (boundary.length > 0) {
            // Set result only when it touches a boundary.
            if (maybePostfix.startsWith(boundary)) {
                result = maybePostfix;
            }
        } else {
            result = maybePostfix; // whatever postfix we found will be the result
        }

        knownPostfix = maybePostfix;
    }

    if (!result && knownPostfix) {
        // An empty result indicates that the postfix stopped before it reached a boundary.
        return knownPostfix;
    }

    return result;
}

/**
 * Finds the common prefix of all the entries, then returns an array that contains all the elements with the prefix
 * removed.
 * @param arr the strings to be processed
 * @param boundary specifies a character that defines a boundary for the prefix. A prefix cannot cross such a boundary
 *    unless it reaches another boundary beyond.
 * @return The given array with any prefix they share removed. If the given strings do not share any common prefix, the
 *    original array will be returned. Note that the result may contain empty strings, indicating that these entries are
 *    nothing other than the common prefix.
 */
export function stripCommonPrefix(arr: string[], boundary: string = ''): string[] {
    const prefix = getCommonPrefix(arr, boundary);
    return !prefix ? arr : arr.map(entry => entry.slice(prefix.length));
}

/**
 * Finds the common postfix of all the entries, then returns an array that contains all the elements with the postfix
 * removed.
 * @param arr the strings to be processed
 * @param boundary specifies a character that defines a boundary for the postfix. A postfix cannot cross such a boundary
 *    unless it reaches another boundary beyond.
 * @return The given array with any postfix they share removed. If the given strings do not share any common postfix,
 *    the original array will be returned. Note that the result may contain empty strings, indicating that these entries
 *    are nothing other than the common postfix.
 */
export function stripCommonPostfix(arr: string[], boundary: string = ''): string[] {
    const postfix = getCommonPostfix(arr, boundary);
    return !postfix ? arr : arr.map(entry => entry.slice(0, entry.length - postfix.length));
}

/**
 * Finds the distinct values in the given array. We don't use Imm.Set here, because on the one hand, we want to detect
 * whether we can return the same instance (no change), while on the other hand, we want to preserve the order of the
 * members.
 * @return The distinct values in the given array. The order is defined by the order each member occurs first in the
 *    given array.
 */
export function deduplicate(arr: string[]): string[] {
    if (!arr.length) return arr;

    const map: { [s: string]: true } = {};
    let anyCollision = false;
    const result: string[] = [];

    for (const s of arr) {
        const key = `${s}_`; // because s may be empty
        if (map[key]) {
            anyCollision = true;
        } else {
            map[key] = true;
            result.push(s);
        }
    }

    // If no collisions were found, return the same instance (better for comparisons that may happen later).
    return !anyCollision ? arr : result;
}
